import React, { useState, useEffect, useCallback } from 'react';
import { FaFilePdf } from 'react-icons/fa';
import Select from 'react-select';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { formatMiningDate, toApiDateString } from '../../../utils/dateUtils';
import api from '../../../services/api';
import UnifiedDateRangePicker from '../../common/UnifiedDateRangePicker';

// Helper to format numbers with commas
const formatNumberWithCommas = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  return parseFloat(num).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Helper to format integers with commas (no decimal places)
const formatIntegerWithCommas = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  return parseInt(num, 10).toLocaleString('en-US');
};

// Helper to format locations for react-select
const formatLocationsForSelect = (locations) => {
  return locations.map((loc) => ({ value: loc, label: loc }));
};

// Progress indicator component
const ProgressIndicator = ({ message, progress }) => (
  <div className="bg-surface p-6 rounded-lg shadow-md">
    <div className="flex items-center space-x-4">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900">{message}</p>
        {progress !== undefined && (
          <div className="mt-2">
            <div className="bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">{progress}% complete</p>
          </div>
        )}
      </div>
    </div>
  </div>
);

const PDFGeneratorV2 = () => {
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (now.getDate() === 1) {
      return fmt(new Date(now.getFullYear(), now.getMonth() - 1, 1));
    }
    return fmt(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (now.getDate() === 1) {
      return fmt(new Date(now.getFullYear(), now.getMonth(), 0));
    }
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return fmt(yesterday);
  });
  // Use state for selected locations (arrays)
  const [selectedDispatchLocations, setSelectedDispatchLocations] = useState([]);
  const [selectedReceiptLocations, setSelectedReceiptLocations] = useState([]);
  // State to hold options for the dropdowns
  const [dispatchLocationOptions, setDispatchLocationOptions] = useState([]);
  const [receiptLocationOptions, setReceiptLocationOptions] = useState([]);
  const [reportData, setReportData] = useState([]); // Initialize as empty array
  const [signatures, setSignatures] = useState([]); // Initialize as empty array
  const [loading, setLoading] = useState(false); // General loading state
  const [locationsLoading, setLocationsLoading] = useState(false); // Specific loading for locations
  const [error, setError] = useState('');
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(''); // State for PDF preview URL
  const [pdfFilename, setPdfFilename] = useState(''); // State for PDF filename
  const [nettWeightThreshold, setNettWeightThreshold] = useState(5); // State for Nett Weight threshold
  const [logoOptions, setLogoOptions] = useState([]); // State for logo dropdown options
  const [selectedLogo, setSelectedLogo] = useState(null); // State for selected logo
  const [logosLoading, setLogosLoading] = useState(false); // Loading state for logos

  // Groupings state
  const [groupings, setGroupings] = useState([]);
  const [selectedGrouping, setSelectedGrouping] = useState(null);
  const [groupingsLoading, setGroupingsLoading] = useState(false);

  // Progress state
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  // Fetch dispatch locations
  const fetchDispatchLocations = useCallback(async () => {
    setLocationsLoading(true);
    try {
      const response = await api.data.getDispatchLocations();
      const options = formatLocationsForSelect(response.data);
      setDispatchLocationOptions(options);
    } catch (err) {
      console.error('Error fetching dispatch locations:', err);
      setError('Failed to load dispatch locations.');
    } finally {
      setLocationsLoading(false);
    }
  }, []);

  // Fetch receipt locations (optionally filtered by dispatch)
  const fetchReceiptLocations = useCallback(async (dispatchLocation = null) => {
    setLocationsLoading(true);
    try {
      const response = await api.data.getReceiptLocations(dispatchLocation);
      const options = formatLocationsForSelect(response.data);
      setReceiptLocationOptions(options);
    } catch (err) {
      console.error('Error fetching receipt locations:', err);
      setError('Failed to load receipt locations.');
    } finally {
      setLocationsLoading(false);
    }
  }, []);

  // Fetch logos
  const fetchLogos = useCallback(async () => {
    setLogosLoading(true);
    try {
      const response = await api.logos.getAll();
      const logoOptions = response.data.map((logo) => ({
        value: logo.id,
        label: logo.name,
        logoPath: logo.logoPath, // Include the logo path for PDF generation
      }));
      setLogoOptions(logoOptions);
    } catch (err) {
      console.error('Error fetching logos:', err);
      setError('Failed to load logos.');
    } finally {
      setLogosLoading(false);
    }
  }, []);

  // Fetch groupings
  const fetchGroupings = useCallback(async () => {
    setGroupingsLoading(true);
    setError('');
    try {
      const response = await api.routeSummary.getGroupings(startDate, endDate);
      const groupingOptions = response.data.map((grouping) => ({
        value: grouping.id,
        label: grouping.name,
        dispatchLocations: grouping.dispatchLocations,
        receiptLocations: grouping.receiptLocations,
      }));
      setGroupings(groupingOptions);
    } catch (err) {
      console.error('Error fetching groupings:', err);
      setError('Failed to load groupings.');
      setGroupings([]);
    } finally {
      setGroupingsLoading(false);
    }
  }, [startDate, endDate]);

  // Initial fetch for locations and logos
  useEffect(() => {
    fetchDispatchLocations();
    fetchReceiptLocations();
    fetchLogos(); // Fetch logos on mount
    if (startDate && endDate) {
      fetchGroupings(); // Fetch groupings when dates are set
    }
  }, [
    fetchDispatchLocations,
    fetchReceiptLocations,
    fetchLogos,
    fetchGroupings,
    startDate,
    endDate,
  ]);

  // Handle grouping selection
  const handleGroupingChange = (selectedOption) => {
    setSelectedGrouping(selectedOption);

    if (selectedOption) {
      // Auto-populate locations from the grouping
      const dispatchOptions = selectedOption.dispatchLocations.map((loc) => ({
        value: loc,
        label: loc,
      }));
      const receiptOptions = selectedOption.receiptLocations.map((loc) => ({
        value: loc,
        label: loc,
      }));

      setSelectedDispatchLocations(dispatchOptions);
      setSelectedReceiptLocations(receiptOptions);
    }
  };

  const handleGeneratePDF = async () => {
    // Check if arrays have at least one selection
    if (
      !startDate ||
      !endDate ||
      selectedDispatchLocations.length === 0 ||
      selectedReceiptLocations.length === 0
    ) {
      setError(
        'Please select start date, end date, at least one dispatch location, and at least one receipt location.'
      );
      return;
    }

    setLoading(true);
    setError('');
    setPdfPreviewUrl(''); // Clear previous preview
    setPdfFilename(''); // Clear previous filename
    setProgress(0);
    setProgressMessage('Initializing PDF generation...');

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev < 90) {
            return prev + 10;
          }
          return prev;
        });
      }, 200);

      setProgressMessage('Fetching data and generating summary...');

      let summaryData, signatures;

      if (selectedGrouping) {
        // Use Route Summary API with grouping
        console.log('Using grouping for PDF generation:', selectedGrouping);

        const summaryResponse = await api.routeSummary.generateSummary({
          groupingId: selectedGrouping.value,
          startDate,
          endDate,
          minWeight: nettWeightThreshold,
        });

        // Transform Route Summary data to PDF format
        summaryData = summaryResponse.data.summary.map((item) => ({
          ReportDate: item.date,
          DispatchTonnes: item.dispatchWeight,
          DispatchTrucks: item.dispatchCount,
          ReceiptTonnes: item.receiptWeight,
          ReceiptTrucks: item.receiptCount,
        }));

        // Add pre-load data if available
        if (summaryResponse.data.preload && summaryResponse.data.preload.dispatchWeight > 0) {
          const preloadData = {
            ReportDate: summaryResponse.data.preload.date,
            DispatchTonnes: summaryResponse.data.preload.dispatchWeight,
            DispatchTrucks: summaryResponse.data.preload.dispatchCount,
            ReceiptTonnes: 0, // Pre-load only has dispatch data
            ReceiptTrucks: 0,
            isPreload: true, // Flag to identify pre-load data
          };
          summaryData.unshift(preloadData); // Add to beginning
        }

        // Fetch signatures for the grouping
        const groupingSignatureResponse = await api.get(
          `/pdf-report-data/grouping-signatures/${selectedGrouping.value}`
        );
        const groupingSignatures = groupingSignatureResponse.data.signatures || [];

        // Also fetch location-based signatures for the grouping locations
        const dispatchValues = selectedGrouping.dispatchLocations;
        const receiptValues = selectedGrouping.receiptLocations;

        const locationSignatureResponse = await api.get('/pdf-report-data', {
          params: {
            startDate,
            endDate,
            dispatchLocation: dispatchValues.join(','),
            receiptLocation: receiptValues.join(','),
            nettWeightThreshold,
          },
        });
        const locationSignatures = locationSignatureResponse.data.signatures || [];

        // Combine and deduplicate signatures
        // Using Name-LastName as key (backend maps SignatureTitle to both LastName and Company)
        const allSignatures = [...groupingSignatures, ...locationSignatures];
        const uniqueSignatures = Array.from(
          new Map(allSignatures.map((sig) => [`${sig.Name}-${sig.LastName}`, sig])).values()
        );
        signatures = uniqueSignatures;
      } else {
        // Use original PDF report API for manual selection
        const dispatchValues = selectedDispatchLocations.map((option) => option.value);
        const receiptValues = selectedReceiptLocations.map((option) => option.value);

        console.log('Using manual location selection for PDF generation');

        const response = await api.get('/pdf-report-data', {
          params: {
            startDate,
            endDate,
            dispatchLocation: dispatchValues.join(','),
            receiptLocation: receiptValues.join(','),
            nettWeightThreshold,
          },
        });

        summaryData = response.data.reportData || [];
        signatures = response.data.signatures || [];
      }

      clearInterval(progressInterval);
      setProgress(100);
      setProgressMessage('Generating PDF...');

      // Update state with the data received from the backend
      setReportData(summaryData);
      setSignatures(signatures);
      console.log('Data received for PDF generation:', { summaryData, signatures });

      // Generate PDF and get data URI for preview
      const { dataUri, filename } = await generatePdfDataUri(
        summaryData,
        signatures,
        selectedDispatchLocations.map((option) => option.value),
        selectedReceiptLocations.map((option) => option.value),
        startDate,
        endDate,
        selectedLogo,
        selectedGrouping
      );

      setPdfPreviewUrl(dataUri);
      setPdfFilename(filename);

      // Small delay to show completion
      setTimeout(() => {
        setProgress(0);
        setProgressMessage('');
      }, 500);
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF. ' + (err.response?.data?.message || err.message));
      setProgress(0);
      setProgressMessage('');
    } finally {
      setLoading(false);
    }
  };

  // Cancel PDF generation
  const handleCancelGeneration = () => {
    setLoading(false);
    setProgress(0);
    setProgressMessage('');
  };

  // Generates PDF and returns data URI and filename - updated for multiple locations, logo, formatting
  const generatePdfDataUri = async (
    data,
    signatureData,
    dispatchLocs,
    receiptLocs,
    startDt,
    endDt,
    logoInfo,
    groupingInfo
  ) => {
    const doc = new jsPDF();
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10; // Reduced margin from 14 to 10
    let currentY = 5; // Reduced top margin from 8 to 5
    let logoHeight = 0; // Height occupied by the logo (or error message)
    let titleBlockHeight = 0; // Height occupied by the title and date

    // --- Define Title and Date Range ---
    let reportTitle;
    if (groupingInfo && groupingInfo.label) {
      // Use grouping name if selected
      reportTitle = `Recon for ${groupingInfo.label}`;
    } else {
      // Use location names if no grouping selected
      const dispatchText = dispatchLocs.length > 1 ? 'Multiple Locations' : dispatchLocs[0];
      const receiptText = receiptLocs.length > 1 ? 'Multiple Locations' : receiptLocs[0];
      reportTitle = `Recon for ${dispatchText} to ${receiptText}`;
    }
    const dateRangeText = `${formatMiningDate(startDt, { day: '2-digit', month: 'short', year: 'numeric' })} - ${formatMiningDate(endDt, { day: '2-digit', month: 'short', year: 'numeric' })}`; // Renamed

    // --- Logo Handling ---
    if (logoInfo && logoInfo.value) {
      try {
        // Fetch logo as Base64 Data URI from the backend
        const logoApiUrl = `${import.meta.env.REACT_APP_API_URL || 'http://localhost:5002'}/api/logos/${logoInfo.value}/base64`;

        console.log('Fetching logo Base64 from:', logoApiUrl);

        const logoResponse = await fetch(logoApiUrl);
        if (!logoResponse.ok) {
          throw new Error(
            `Failed to fetch logo: ${logoResponse.status} ${logoResponse.statusText}`
          );
        }

        const logoData = await logoResponse.json();
        const dataUri = logoData.dataUri;

        console.log('Logo Base64 Data URI received, length:', dataUri.length);

        // Create an image element to get dimensions
        const img = new Image();

        // Wait for the image to load from data URI
        await new Promise((resolve, reject) => {
          img.onload = () => {
            try {
              console.log('Logo loaded successfully, dimensions:', img.width, 'x', img.height);

              // Calculate logo dimensions (max width 50, maintain aspect ratio)
              const maxLogoWidth = 50;
              const aspectRatio = img.height / img.width;
              const logoWidth = Math.min(maxLogoWidth, img.width);
              const calculatedLogoHeight = logoWidth * aspectRatio;

              // Determine image format from data URI
              let imageFormat = 'PNG'; // Default
              if (dataUri.includes('image/jpeg') || dataUri.includes('image/jpg')) {
                imageFormat = 'JPEG';
              } else if (dataUri.includes('image/svg')) {
                imageFormat = 'SVG';
              }

              // Add logo to PDF (top-right corner)
              const logoX = pageWidth - margin - logoWidth;
              const logoY = currentY;
              doc.addImage(dataUri, imageFormat, logoX, logoY, logoWidth, calculatedLogoHeight);

              logoHeight = calculatedLogoHeight + 5; // Add some spacing
              console.log('Logo added to PDF successfully');
              resolve();
            } catch (err) {
              console.error('Error adding logo to PDF:', err);
              // Add error message instead of logo
              doc.setFontSize(8);
              doc.setTextColor(255, 0, 0); // Red color for error
              doc.text('Logo load error', pageWidth - margin - 30, currentY + 10);
              logoHeight = 15;
              resolve();
            }
          };

          img.onerror = (error) => {
            console.error('Failed to load logo from data URI:', error);
            // Add error message instead of logo
            doc.setFontSize(8);
            doc.setTextColor(255, 0, 0); // Red color for error
            doc.text('Logo not found', pageWidth - margin - 30, currentY + 10);
            logoHeight = 15;
            resolve();
          };

          img.src = dataUri;
        });
      } catch (err) {
        console.error('Error processing logo:', err);
        // Add error message
        doc.setFontSize(8);
        doc.setTextColor(255, 0, 0);
        doc.text('Logo error', pageWidth - margin - 30, currentY + 10);
        logoHeight = 15;
      }
    }

    // Reset text color to black for the rest of the document
    doc.setTextColor(0, 0, 0);

    // --- Title and Date Block ---
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    const titleY = currentY + Math.max(logoHeight / 2, 10); // Center title with logo or use default spacing
    doc.text(reportTitle, margin, titleY);

    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    const dateY = titleY + 8;
    doc.text(dateRangeText, margin, dateY);

    titleBlockHeight = Math.max(logoHeight, dateY - currentY + 5); // Height of title block
    currentY += titleBlockHeight + 5; // Reduced spacing from 10 to 5

    // --- Table Generation ---
    const tableHeaders = [
      'Date',
      'Dispatch\nTonnes',
      'Dispatch\nTrucks',
      'Receipt\nTonnes',
      'Receipt\nTrucks',
      'Variance\nTonnes',
      'Variance\n%',
    ];
    const tableRows = [];
    let totalDispatchTonnes = 0;
    let totalDispatchTrucks = 0;
    let totalReceiptTonnes = 0;
    let totalReceiptTrucks = 0;

    data.forEach((item, index) => {
      const dispatchTonnes = parseFloat(item.DispatchTonnes || 0);
      const receiptTonnes = parseFloat(item.ReceiptTonnes || 0);
      const dispatchTrucks = parseInt(item.DispatchTrucks || 0, 10);
      const receiptTrucks = parseInt(item.ReceiptTrucks || 0, 10);

      const varianceTonnes = receiptTonnes - dispatchTonnes;
      // Handle potential division by zero or NaN for variance percentage
      let variancePercent = 0;
      if (dispatchTonnes !== 0) {
        variancePercent = (varianceTonnes / dispatchTonnes) * 100;
      } else if (varianceTonnes !== 0) {
        variancePercent = Infinity; // Or handle as 'N/A' or specific text
      }

      // Format date with special handling for pre-load
      let dateDisplay = formatMiningDate(item.ReportDate);
      if (item.isPreload) {
        dateDisplay += ' (Brought Forward)';
      }

      const rowData = [
        dateDisplay,
        formatNumberWithCommas(dispatchTonnes), // Use helper
        formatIntegerWithCommas(dispatchTrucks), // Use helper
        formatNumberWithCommas(receiptTonnes), // Use helper
        formatIntegerWithCommas(receiptTrucks), // Use helper
        formatNumberWithCommas(varianceTonnes), // Use helper
        // Handle Infinity case for display
        variancePercent === Infinity ? 'Inf%' : formatNumberWithCommas(variancePercent) + '%',
      ];
      tableRows.push(rowData);

      // Accumulate totals
      totalDispatchTonnes += dispatchTonnes;
      totalDispatchTrucks += dispatchTrucks;
      totalReceiptTonnes += receiptTonnes;
      totalReceiptTrucks += receiptTrucks;
    });

    // Calculate total variance
    const totalVarianceTonnes = totalReceiptTonnes - totalDispatchTonnes;
    let totalVariancePercent = 0;
    if (totalDispatchTonnes !== 0) {
      totalVariancePercent = (totalVarianceTonnes / totalDispatchTonnes) * 100;
    } else if (totalVarianceTonnes !== 0) {
      totalVariancePercent = Infinity;
    }

    // Add totals row
    const totalsRow = [
      'TOTAL',
      formatNumberWithCommas(totalDispatchTonnes),
      formatIntegerWithCommas(totalDispatchTrucks),
      formatNumberWithCommas(totalReceiptTonnes),
      formatIntegerWithCommas(totalReceiptTrucks),
      formatNumberWithCommas(totalVarianceTonnes),
      totalVariancePercent === Infinity
        ? 'Inf%'
        : formatNumberWithCommas(totalVariancePercent) + '%',
    ];
    tableRows.push(totalsRow);

    // Generate table using autoTable with ultra-compact styling to fit 32 rows
    doc.autoTable({
      head: [tableHeaders],
      body: tableRows,
      startY: currentY,
      theme: 'striped',
      styles: {
        fontSize: 6.5,
        cellPadding: { top: 1, right: 1, bottom: 1, left: 1 },
        halign: 'center',
        valign: 'middle',
        lineColor: [220, 220, 220],
        lineWidth: 0.2,
        font: 'helvetica',
        minCellHeight: 5,
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 6.5,
        cellPadding: { top: 1.5, right: 1, bottom: 1.5, left: 1 },
        minCellHeight: 7,
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [44, 62, 80],
        fontSize: 6,
        minCellHeight: 5,
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250],
      },
      didParseCell: function (data) {
        if (data.row.index === tableRows.length - 1) {
          data.cell.styles.fillColor = [52, 73, 94];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 6.5;
          data.cell.styles.minCellHeight = 7;
        }
      },
      margin: { left: margin, right: margin },
      tableWidth: 'wrap',
      columnStyles: {
        0: {
          halign: 'left',
          cellWidth: 26,
        },
        1: {
          halign: 'right',
          cellWidth: 19,
        },
        2: {
          halign: 'right',
          cellWidth: 15,
        },
        3: {
          halign: 'right',
          cellWidth: 19,
        },
        4: {
          halign: 'right',
          cellWidth: 15,
        },
        5: {
          halign: 'right',
          cellWidth: 19,
        },
        6: {
          halign: 'right',
          cellWidth: 15,
        },
      },
    });

    // Update currentY to position after the table with a bit more spacing
    currentY = doc.lastAutoTable.finalY + 6;

    // --- Modern Signatures Section ---
    if (signatureData && signatureData.length > 0) {
      // Calculate space needed for signatures in 3-column block layout
      const signaturesPerRow = 3;
      const signatureBlockHeight = 21; // Increased from 18 to 21 for more space between rows
      const rows = Math.ceil(signatureData.length / signaturesPerRow);
      const signaturesHeight = 25 + rows * signatureBlockHeight; // Reduced from 35 to 25

      // Check if we need a new page for signatures (using minimal bottom margin)
      if (currentY + signaturesHeight > pageHeight - 5) {
        doc.addPage();
        currentY = margin;
      }

      // Modern signature section header
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(52, 73, 94);
      doc.text('Required Signatures', margin, currentY);

      // Add a modern underline with gradient effect
      doc.setDrawColor(41, 128, 185);
      doc.setLineWidth(1);
      doc.line(margin, currentY + 2, margin + 60, currentY + 2);
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.3);
      doc.line(margin + 60, currentY + 2, pageWidth - margin, currentY + 2);

      currentY += 12;

      // Create signature blocks in a 3-column layout
      const blockWidth = (pageWidth - margin * 2 - 12) / 3; // 6px gap between blocks
      const blockHeight = 18;

      signatureData.forEach((signature, index) => {
        const col = index % signaturesPerRow;
        const row = Math.floor(index / signaturesPerRow);

        const blockX = margin + col * (blockWidth + 6);
        const blockY = currentY + row * signatureBlockHeight;

        // Draw modern signature block with rounded corners effect
        doc.setDrawColor(180, 180, 180);
        doc.setLineWidth(0.4);
        doc.setFillColor(248, 249, 250);
        doc.roundedRect(blockX, blockY, blockWidth, blockHeight, 1.5, 1.5, 'FD');

        // Name and title text (SignatureTitle is mapped to both LastName and Company, so only show once)
        doc.setFontSize(6.5);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(52, 73, 94);
        const nameText = signature.Name || '';
        doc.text(nameText, blockX + 2, blockY + 4);

        doc.setFontSize(5.5);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(100, 100, 100);
        // Show title/company only once (LastName and Company are the same value from SignatureTitle)
        const titleText = signature.LastName || signature.Company || '';
        doc.text(titleText, blockX + 2, blockY + 8);

        // Signature line
        doc.setDrawColor(120, 120, 120);
        doc.setLineWidth(0.3);
        doc.line(blockX + 2, blockY + 12, blockX + blockWidth - 15, blockY + 12);

        // Date field
        doc.setFontSize(5);
        doc.setTextColor(100, 100, 100);
        doc.text('Date:', blockX + blockWidth - 13, blockY + 11);
        doc.line(blockX + blockWidth - 10, blockY + 12, blockX + blockWidth - 2, blockY + 12);

        // Signature label
        doc.setFontSize(4.5);
        doc.setTextColor(120, 120, 120);
        doc.text('Signature', blockX + 2, blockY + 15);
      });

      // Update currentY to after all signature blocks with additional spacing
      const totalRows = Math.ceil(signatureData.length / signaturesPerRow);
      currentY += totalRows * signatureBlockHeight + 8;
    }

    // Generate filename
    const formattedStartDate = toApiDateString(startDt);
    const formattedEndDate = toApiDateString(endDt);
    let filename;
    if (groupingInfo && groupingInfo.label) {
      filename = `Recon_${groupingInfo.label.replace(/\s+/g, '_')}_${formattedStartDate}_to_${formattedEndDate}.pdf`;
    } else {
      const dispatchText =
        dispatchLocs.length > 1 ? 'Multiple_Locations' : dispatchLocs[0].replace(/\s+/g, '_');
      const receiptText =
        receiptLocs.length > 1 ? 'Multiple_Locations' : receiptLocs[0].replace(/\s+/g, '_');
      filename = `Recon_${dispatchText}_to_${receiptText}_${formattedStartDate}_to_${formattedEndDate}.pdf`;
    }

    // Return data URI and filename
    return {
      dataUri: doc.output('datauristring'),
      filename: filename,
    };
  };

  // Download PDF function
  const handleDownloadPdf = () => {
    if (pdfPreviewUrl && pdfFilename) {
      const link = document.createElement('a');
      link.href = pdfPreviewUrl;
      link.download = pdfFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page Header */}
      <div className="sticky top-0 z-10 bg-surface border-b border-slate-200">
        <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-3">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <div className="hidden sm:flex items-center justify-center w-11 h-11 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-sm flex-shrink-0">
                <FaFilePdf className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-bold text-slate-800 tracking-tight">
                  PDF Generator
                </h1>
                <p className="text-slate-500 text-sm hidden sm:block">
                  Generate and preview reconciliation PDF reports
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="bg-surface p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">Generate PDF Report</h2>

          {error && (
            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Date Range Section */}
          <div className="mb-6 pb-6 border-b border-gray-200">
            <div className="mb-4">
              <h3 className="text-sm font-bold text-gray-900 mb-1">Report Period</h3>
              <p className="text-xs text-gray-600">Select date range for PDF generation</p>
            </div>
            <div className="flex justify-start">
              <UnifiedDateRangePicker
                startDate={startDate}
                onStartDateChange={(date) => {
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  setStartDate(`${year}-${month}-${day}`);
                }}
                endDate={endDate}
                onEndDateChange={(date) => {
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  setEndDate(`${year}-${month}-${day}`);
                }}
                disabled={loading}
              />
            </div>
          </div>

          {/* Configuration Section */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Report Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Grouping Selector */}
              <div className="md:col-span-2">
                <label htmlFor="groupingSelect" className="block text-sm font-medium text-gray-700">
                  Select Grouping (Optional)
                </label>
                <Select
                  id="groupingSelect"
                  options={groupings}
                  value={selectedGrouping}
                  onChange={handleGroupingChange}
                  isLoading={groupingsLoading}
                  placeholder={
                    groupingsLoading
                      ? 'Loading Groupings...'
                      : 'Select a Grouping to auto-fill locations...'
                  }
                  isClearable
                  className="mt-1"
                  classNamePrefix="select"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Select a grouping to automatically populate dispatch and receipt locations
                </p>
              </div>

              {/* Location Dropdowns */}
              <div>
                <label htmlFor="dispatchSelect" className="block text-sm font-medium text-gray-700">
                  Dispatch Locations
                </label>
                <Select
                  id="dispatchSelect"
                  isMulti
                  options={dispatchLocationOptions}
                  value={selectedDispatchLocations}
                  onChange={setSelectedDispatchLocations}
                  isLoading={locationsLoading}
                  placeholder={locationsLoading ? 'Loading...' : 'Select Dispatch Locations'}
                  className="mt-1"
                  classNamePrefix="select"
                />
              </div>
              <div>
                <label htmlFor="receiptSelect" className="block text-sm font-medium text-gray-700">
                  Receipt Locations
                </label>
                <Select
                  id="receiptSelect"
                  isMulti
                  options={receiptLocationOptions}
                  value={selectedReceiptLocations}
                  onChange={setSelectedReceiptLocations}
                  isLoading={locationsLoading}
                  placeholder={locationsLoading ? 'Loading...' : 'Select Receipt Locations'}
                  className="mt-1"
                  classNamePrefix="select"
                />
              </div>

              {/* Nett Weight Threshold */}
              <div>
                <label
                  htmlFor="nettWeightThreshold"
                  className="block text-sm font-medium text-gray-700"
                >
                  Nett Weight Threshold
                </label>
                <input
                  type="number"
                  id="nettWeightThreshold"
                  value={nettWeightThreshold}
                  onChange={(e) => setNettWeightThreshold(parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.1"
                  className="mt-1 block w-full px-3 py-2 border border-slate-200 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500/25 focus:border-primary-500 sm:text-sm"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Only include records with nett weight above this threshold
                </p>
              </div>

              {/* Logo Selector */}
              <div>
                <label htmlFor="logoSelect" className="block text-sm font-medium text-gray-700">
                  Logo (Optional)
                </label>
                <Select
                  id="logoSelect"
                  options={logoOptions}
                  value={selectedLogo}
                  onChange={setSelectedLogo}
                  isLoading={logosLoading}
                  placeholder={logosLoading ? 'Loading Logos...' : 'Select a Logo (Optional)'}
                  isClearable
                  className="mt-1"
                  classNamePrefix="select"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Select a logo to include in the PDF header
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center mb-4 space-x-4">
            <button
              onClick={handleGeneratePDF}
              disabled={loading}
              className="bg-primary-500 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
            >
              {loading ? 'Generating...' : 'Generate & Preview PDF'}
            </button>
            {pdfPreviewUrl && (
              <button
                onClick={handleDownloadPdf}
                className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
              >
                Download PDF
              </button>
            )}
          </div>

          {/* Progress Indicator */}
          {loading && progressMessage && (
            <div className="mt-4">
              <ProgressIndicator message={progressMessage} progress={progress} />
              <div className="mt-2 flex justify-center">
                <button
                  onClick={handleCancelGeneration}
                  className="px-4 py-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                >
                  Cancel Generation
                </button>
              </div>
            </div>
          )}

          {/* PDF Preview Area */}
          {pdfPreviewUrl && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-2 text-gray-700">PDF Preview</h3>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <iframe
                  src={pdfPreviewUrl}
                  width="100%"
                  height="600px"
                  title="PDF Preview"
                  className="border-0"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PDFGeneratorV2;
